import CryptoJS from 'crypto-js';
import pako from 'pako';

// 预生成 CRC32 表
const crcTable = (() => {
    const table: number[] = [];
    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) {
            c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[i] = c;
    }
    return table;
})();

// CRC32 函数
function abcdefg(str: string): number {
    let crc = 0 ^ (-1);

    for (let i = 0; i < str.length; i++) {
        crc = (crc >>> 8) ^ crcTable[(crc ^ str.charCodeAt(i)) & 0xff];
    }

    return (crc ^ (-1)) >>> 0;
}

// 生成UUID
export function genUUID(): string {
    let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
    // 获取当前时间戳（秒），转换为16进制，并截取前8位
    const timestampHex = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');

    // 替换 UUID 的指定位置字符
    const replacePositions = [6, 7, 11, 12, 16, 17, 21, 22];
    let uuidArray = uuid.split(''); // 将 UUID 转为字符数组便于替换

    for (let i = 0; i < replacePositions.length; i++) {
        uuidArray[replacePositions[i]] = timestampHex[i];
    }
    // 将字符数组重新拼接成字符串
    uuid = uuidArray.join('');

    // 对 UUID 除去后四位的部分计算 CRC32
    const crc = abcdefg(uuid.slice(0, -4)).toString(16).padStart(4, '0').slice(-4);
    // 将 UUID 的后四位替换为 CRC32 值
    uuid = uuid.slice(0, -4) + crc;
    return uuid;
}

// 计算MD5 hash
export function genBodyMD(data: Uint8Array): string {
    const wordArray = bytesToWordArray(data);
    const hash = CryptoJS.MD5(wordArray);
    return hash.toString(CryptoJS.enc.Hex).toUpperCase();
}

// Constants from the original code
const magic = [0x74, 0x63, 0x05, 0x10, 0x00, 0x00];
const salt = [
    0x4D, 0xD4, 0xC2, 0xE6, 0xB8, 0x31, 0x62, 0x09, 0x0E, 0x52, 0xB3, 0xC7, 0xA6, 0x73, 0x3B, 0xA4,
    0x1C, 0xB2, 0x46, 0x2B, 0x82, 0x9A, 0xB5, 0x8A, 0x19, 0x6B, 0x39, 0xDB, 0x57, 0x17, 0x75, 0x24,
    0xF4, 0x9B, 0xAF, 0x7F, 0x08, 0xE8, 0xD6, 0x8D, 0x26, 0xA7, 0x2E, 0x37, 0xC1, 0xA9, 0x5A, 0x2F,
    0x1F, 0x05, 0xA5, 0x18, 0x92, 0xAE, 0xF2, 0x94, 0x97, 0x32, 0xB6, 0x2A, 0x38, 0xAA, 0xDD, 0x58,
];

// Helper function to convert byte array to crypto-js WordArray
function bytesToWordArray(bytes: Uint8Array | number[]): CryptoJS.lib.WordArray {
    const words: number[] = [];
    for (let i = 0; i < bytes.length; i += 4) {
        let word = 0;
        for (let j = 0; j < 4 && i + j < bytes.length; j++) {
            word |= (bytes[i + j] << (24 - j * 8));
        }
        words.push(word);
    }
    return CryptoJS.lib.WordArray.create(words, bytes.length);
}

// Helper function to convert crypto-js WordArray to byte array
function wordArrayToBytes(wordArray: CryptoJS.lib.WordArray): number[] {
    const bytes: number[] = [];
    for (let i = 0; i < wordArray.words.length; i++) {
        const word = wordArray.words[i];
        for (let j = 0; j < 4; j++) {
            const byteIndex = i * 4 + j;
            if (byteIndex < wordArray.sigBytes) {
                bytes.push((word >>> (24 - j * 8)) & 0xFF);
            }
        }
    }
    return bytes;
}

// Generate key and IV using crypto-js
function genKeyAndIv(input: number[]): [CryptoJS.lib.WordArray, CryptoJS.lib.WordArray] {
    // Convert input array to WordArray
    const inputWordArray = bytesToWordArray(input);

    // SHA-512 hash
    const sha512Hash = CryptoJS.SHA512(inputWordArray);

    // Mix with salt
    const saltWordArray = bytesToWordArray(salt);
    const mix = sha512Hash.concat(saltWordArray);

    // Final SHA-256 hash
    const finalHash = CryptoJS.SHA256(mix);

    // Extract first 16 bytes for AES key and next 16 bytes for IV
    const finalHashWords = finalHash.words;
    const aesKey = CryptoJS.lib.WordArray.create(finalHashWords.slice(0, 4), 16); // 16 bytes = 4 words
    const aesIv = CryptoJS.lib.WordArray.create(finalHashWords.slice(4, 8), 16);  // 16 bytes = 4 words

    return [aesKey, aesIv];
}

// Generate random key
function generateRandomKey(): number[] {
    const randomKey: number[] = [];
    for (let i = 0; i < 32; i++) {
        randomKey.push(Math.floor(Math.random() * 256));
    }
    return randomKey;
}

// Build request data using crypto-js
export function buildRequestData(input: string): Uint8Array {
    // Convert input to Uint8Array
    const encoder = new TextEncoder();
    const rawData = encoder.encode(input);

    // Compress using pako (gzip)
    const compressed = pako.gzip(rawData);

    // SHA-256 hash of compressed data
    const compressedWordArray = bytesToWordArray(compressed);
    const hash = CryptoJS.SHA256(compressedWordArray);

    // Convert hash to bytes and combine with compressed data
    const hashBytes = wordArrayToBytes(hash);
    const fullPayload = [...hashBytes, ...compressed];

    // Generate random key and derive AES key/IV
    const randomKey = generateRandomKey();
    const [aesKey, aesIv] = genKeyAndIv(randomKey);

    // Encrypt the payload using AES-128-CBC
    const fullPayloadWordArray = bytesToWordArray(fullPayload);
    const encrypted = CryptoJS.AES.encrypt(fullPayloadWordArray, aesKey, {
        iv: aesIv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    });

    // Convert encrypted data to bytes
    const encryptedBytes = wordArrayToBytes(encrypted.ciphertext);

    // Combine magic, random key, and encrypted data
    return new Uint8Array([...magic, ...randomKey, ...encryptedBytes]);
}

const magic2 = [0x74, 0x63, 0x05, 0x10, 0x01, 0x01];

interface DecryptResult {
    response?: Uint8Array;
    error: string;
}

export function checkAndGetOriginResp(input: Uint8Array, bodyMD: string): DecryptResult {
    const calcBodyMD = genBodyMD(input);
    if (calcBodyMD !== bodyMD) {
        return { error: "response md check error" };
    }

    if (input.length < 7) {
        return { error: "invalid input length" };
    }

    // 校验 magic
    for (let i = 0; i < magic2.length; i++) {
        if (input[i] !== magic2[i]) {
            return { error: "invalid magic header" };
        }
    }

    // 跳过 magic 部分
    let offset = magic2.length;

    // 读取 key 长度
    const keyLen = input[offset];
    offset += 1;

    if (input.length <= offset + keyLen + 32) {
        return { error: "input too short for key and hash" };
    }

    // 获取加密 key
    let encKey = input.slice(offset, offset + keyLen);
    offset += keyLen;

    // 解密 key (每个字节异或 0x78)
    let key = Array.from(encKey).map(b => b ^ 0x78);

    // 剩余部分是加密的 payload
    let encryptedPayload = input.slice(offset);

    // 解密 payload (循环异或)
    let decryptedPayload = new Uint8Array(encryptedPayload.length);
    for (let i = 0; i < encryptedPayload.length; i++) {
        decryptedPayload[i] = encryptedPayload[i] ^ key[i % key.length];
    }

    // 拆分 hash 和压缩数据
    const recvHash = decryptedPayload.slice(0, 32);
    const compressed = decryptedPayload.slice(32);

    // 计算压缩数据的 sha256
    const compressedWordArray = bytesToWordArray(compressed);
    const calcHash = CryptoJS.SHA256(compressedWordArray);
    const calcHashBytes = wordArrayToBytes(calcHash);

    for (let i = 0; i < 32; i++) {
        if (recvHash[i] !== calcHashBytes[i]) {
            return { error: "hash mismatch" };
        }
    }

    // 解压缩获取原始数据
    try {
        const decompressed = pako.ungzip(compressed);
        return { response: decompressed, error: "" };
    } catch (e: any) {
        return { error: "decompression failed: " + e.message };
    }
}
